import { getApiUrl } from "@/config/api";

// Bumped to _v2 after the fixed-per-action credit migration so any stale
// per-minute / old-cost caches in a user's browser are ignored on next load.
const CREDIT_COST_CACHE_KEY = "company_action_costs_v2";

class CreditService {

  companyId() {
    return localStorage.getItem("companyAccountId");
  }

  async fetchJson(endpoint) {
    const url = getApiUrl(endpoint);
    let res;

    try {
      res = await fetch(url, { credentials: "include" });
    } catch (error) {
      throw new Error(`Could not reach credit API at ${url}.`);
    }

    let data;
    const contentType = res.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      data = await res.json();
    } else {
      const body = await res.text();
      const shortBody = body.slice(0, 160).trim();
      throw new Error(
        shortBody
          ? `Credit API returned a non-JSON response: ${shortBody}`
          : `Credit API returned a non-JSON response with status ${res.status}.`
      );
    }

    if (!res.ok) {
      throw new Error(
        data?.error || data?.message || `Credit API request failed with status ${res.status}.`
      );
    }

    return data;
  }

  // Load cost config once
  async preloadCosts() {
    const cached = localStorage.getItem(CREDIT_COST_CACHE_KEY);

    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        localStorage.removeItem(CREDIT_COST_CACHE_KEY);
      }
    }

    const companyId = this.companyId();
    if (!companyId) {
      throw new Error("Company account id is missing for credit lookup.");
    }

    const data = await this.fetchJson(
      `/api/credits/action-costs?company_id=${companyId}`
    );

    const map = {};

    data.costs.forEach(c => {
      map[c.action_type] = c;
    });

    localStorage.setItem(
      CREDIT_COST_CACHE_KEY,
      JSON.stringify(map)
    );

    return map;
  }

  async getActionCost(actionType) {

    const cache = await this.preloadCosts();

    return cache[actionType] || null;
  }

  calculateCost(config, units = 1) {

    if (!config) return 0;

    const creditCost = Number(config.credit_cost);
    const unitValue = Number(config.unit_value);

    // Debug log removed — was logging internal billing config type each call.

    switch (config.cost_type) {

      case "fixed":
        return creditCost;

      case "ratio":
        return Number(((units / unitValue) * creditCost).toFixed(2));

      case "per_minute":
        return units * creditCost;

      default:
        return creditCost;
    }
  }

  async getCost(actionType, units = 1) {

    const config = await this.getActionCost(actionType);

    return this.calculateCost(config, units);
  }

  // Balance should always hit backend
  async getBalance() {

    const companyId = this.companyId();
    if (!companyId) {
      throw new Error("Company account id is missing for credit balance lookup.");
    }

    return this.fetchJson(`/api/credits/balance?company_id=${companyId}`);
  }

}

const creditService = new CreditService();

export default creditService;
